import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
    SlackLogoIcon,
    ArrowsCounterClockwiseIcon,
    PencilSimpleIcon,
    DownloadSimpleIcon,
    ArrowSquareOutIcon,
    UploadSimpleIcon,
    EyeIcon,
    TrashIcon,
    PlusIcon,
    DatabaseIcon,
    TableIcon,
    ChartBarIcon,
} from '@phosphor-icons/react'
import {
    ScoopScreenLayout,
    ScoopText,
    ScoopMenu,
    ScoopMenuItem,
    ScoopButton,
    ScoopSkeleton,
    ScoopColumn,
    ScoopCenter,
    useToast,
} from '../../../ui'
import { useModal, MODALS } from '../../../modals'
import { DatasetRow } from '../DatasetRow/DatasetRow'
import { Server } from '../../../../api/Server'
import { NewDatasetModal } from '../NewDatasetModal/NewDatasetModal'
import { APIConnector } from '../../Appconnect/API/APIConnector'
import { APIWriteBack } from '../../Appconnect/API/APIWriteBack'
import { preDelete } from '../../Appconnect/API/APIUIOverrides'
import { useWorkspaceMetadata } from '../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { useGetProcessStatus } from '../../../../api/hooks/datasource/hook'
import { EmptyDataset } from '../../../EmptyDataset/EmptyDataset'
import { useBotActions } from '../../../../api/hooks/botActions/useBotActions'
import { DialogItem } from '../components/DialogItem/DialogItem'
import { useSlack } from '../../../../api/hooks/slack/useSlack'
import { useWorkspaces } from '../../../../api/hooks/workspaces/useWorkspaces'
import { useUserInfo } from '../../../../api/hooks/user/useUserInfo'
import { HelpBotPromptButton } from '../../../HelpBot/components/HelpBotPromptButton/HelpBotPromptButton'
import { HELP_BOT_PROMPTS } from '../../../HelpBot/helpBotPrompts'
import SlackIcon from '../../../../assets/common/Slack_Mark_Web.png'
import { EVENTS, trackEvent } from '../../../../events'
import styles from './DatasetIndex.module.scss'

export const DatasetIndex = () => {
    const location = useLocation()
    const { openNewDataset } = location.state || false
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedInbox, setSelectedInbox] = useState(null)
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)
    const accountID = useSelector((state) => state.auth.accountID)
    const {
        workspaceMetadata,
        isLoading,
        refetch: getMetadata,
        deleteSource,
    } = useWorkspaceMetadata({ tableStats: true })
    const [processStatus, setProcessStatus] = useState(new Map())

    // Modal Manager hooks
    const deleteDatasetModal = useModal(MODALS.COMMON.DELETE)
    const deleteConnectionModal = useModal(MODALS.COMMON.DELETE)
    const disconnectSlackModal = useModal(MODALS.SOURCE.DISCONNECT_SLACK)
    const connectSlackModal = useModal(MODALS.SOURCE.CONNECT_SLACK)
    const apiConnectionsModal = useModal(MODALS.SOURCE.API_CONNECTIONS)
    const configureSlackModal = useModal(MODALS.SOURCE.CONFIGURE_SLACK)
    const tourAlertModal = useModal(MODALS.COMMON.TOUR_ALERT)
    const [openDatasetOptions, setOpenDatasetOptions] = useState(false)
    const [openDatasetSources, setOpenDatasetSources] = useState(openNewDataset)
    const [openBlendedDataset, setOpenBlendedDataset] = useState(false)
    const toast = useToast()
    const [reprocessing, setReprocessing] = useState(false)
    const [reprocessId, setReprocessId] = useState('')
    const [apiOpen, setAPIOpen] = useState(false)
    const [writebackOpen, setWritebackOpen] = useState(false)
    const [datasetSearch, setDatasetSearch] = useState('')
    const [filteredInboxes, setFilteredInboxes] = useState([])
    const [hasConnectionToSlack, setHasConnectionToSlack] = useState(false)
    const { processStatus: rawProcessStatus } = useGetProcessStatus()
    const { runBot, isRunning } = useBotActions()
    const slackConnectionKey =
        workspaceMetadata?.apiConnections?.find((c) => c.connectionType === 'Slack')
            ?.connectionKey || ''
    const { getOAuthUrl, isGettingOAuthUrl, disconnectSlackConnection } = useSlack(
        slackConnectionKey,
        null
    )
    const { workspaces: workspacesAccessible } = useWorkspaces()
    const { user } = useUserInfo()

    const isDefaultWorkspace = user?.defaultTour
        ? user.defaultTour === workspaceID
        : workspacesAccessible.find((w) => w.name === 'Tour Scoop')?.workspaceID === workspaceID

    const datasetOptionsRef = useRef(null)

    useEffect(() => {
        if (workspaceMetadata?.inboxes) {
            setFilteredInboxes(
                workspaceMetadata.inboxes
                    .filter(
                        (inbox) =>
                            inbox.inboxName.toLowerCase().includes(datasetSearch.toLowerCase()) &&
                            inbox.inboxType.toLowerCase() !== 'external'
                    )
                    .sort((a, b) =>
                        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
                    )
            )
        }
        if (workspaceMetadata?.apiConnections) {
            setHasConnectionToSlack(
                workspaceMetadata.apiConnections.some(
                    (connection) => connection.connectionType === 'Slack'
                )
            )
        }
    }, [workspaceMetadata])

    useEffect(() => {
        if (openNewDataset) setOpenDatasetSources(openNewDataset)
    }, [location.state])

    useEffect(() => {
        if (rawProcessStatus) {
            processStatusResult(rawProcessStatus)
        }
    }, [rawProcessStatus])

    const processStatusResult = (result) => {
        const psMap = new Map()
        if (result.inboxStatus.length > 0) {
            result.inboxStatus.forEach((inboxStatusItem) => {
                psMap.set(inboxStatusItem.inboxID, inboxStatusItem)
            })
            setProcessStatus(psMap)
            setReprocessing(true)
        } else {
            setProcessStatus(new Map())
            setReprocessing(false)
        }
    }

    const checkStatus = () => {
        if (token) {
            const server = new Server(workspaceID, userID, token)
            server.postData(
                {
                    action: 'getProcessStatus',
                    workspaceID: workspaceID,
                },
                processStatusResult
            )
        }
    }

    useEffect(() => {
        if (reprocessing) {
            const intervalId = setInterval(checkStatus, 5000)
            setReprocessId(intervalId)
        } else {
            clearInterval(reprocessId)
        }
    }, [reprocessing])

    const handleReprocess = () => {
        const server = new Server(workspaceID, userID, token)
        setAnchorEl(null)
        server.sheetPostData(
            {
                action: 'reProcess',
                inboxID: selectedInbox.inboxID,
            },
            () => {
                checkStatus()
                toast.info('Processing started. Stay tuned.')
            }
        )
        handleClose()
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleDeleteInbox = async () => {
        setAnchorEl(null)
        handleClose()

        await deleteDatasetModal.show({
            title: 'Dataset',
            description: 'Are you sure that you want to delete this dataset from Scoop?',
            onDelete: () =>
                new Promise((resolve, reject) => {
                    deleteSource({
                        inboxID: selectedInbox.inboxID,
                        onSuccess: (response) => {
                            handleAfterDelete(response)
                            resolve()
                        },
                        onError: (response) => {
                            handleError(response)
                            reject()
                        },
                    })
                }),
        })
    }

    const handleDeleteConnection = async (connection) => {
        const server = new Server(workspaceID, userID, token)
        // in case there's something to do before deleting the connection
        await preDelete(
            connection.connectionType,
            workspaceID,
            userID,
            token,
            connection.connectionKey
        )
        server.postData(
            {
                action: 'deleteObject',
                class: 'scoop.connector.APIConnection',
                key: connection.connectionKey,
            },
            (response) => handleAfterDeleteConnection(response, connection),
            {},
            (response) => handleError(response)
        )
    }

    const handleConfirmDeleteConnection = async (connection) => {
        await deleteConnectionModal.show({
            title: 'Connection',
            description: 'Are you sure that you want to delete this connection from Scoop?',
            onDelete: async () => {
                await handleDeleteConnection(connection)
                apiConnectionsModal.hide()
            },
        })
    }

    const handleConnectToSlack = () => {
        trackEvent(EVENTS.DATASET.CONNECT_SLACK)
        getOAuthUrl(
            {},
            {
                onSuccess: (response) => {
                    if (response?.oauthLink) {
                        window.location.href = response.oauthLink
                    } else {
                        toast.error('Failed to get Slack OAuth URL')
                    }
                },
                onError: (error) => {
                    toast.error(error?.message || 'Failed to connect to Slack')
                },
            }
        )
    }

    const handleDisconnectSlack = () => {
        const slackConnection = workspaceMetadata?.apiConnections?.find(
            (c) => c.connectionType === 'Slack'
        )

        if (!slackConnection) {
            toast.error('No Slack connection found')
            return
        }

        disconnectSlackConnection(
            {
                connectionKey: slackConnection.connectionKey,
            },
            {
                onSuccess: (response) => {
                    disconnectSlackModal.close()
                    if (response.error) {
                        toast.error(response?.error || 'Failed to disconnect Slack')
                    } else {
                        toast.success(response.message ?? 'Slack disconnected successfully')
                        getMetadata()
                    }
                },
                onError: (error) => {
                    disconnectSlackModal.close()
                    toast.error(error?.message || 'Failed to disconnect Slack')
                },
            }
        )
    }

    const handleAfterDeleteConnection = (response, connection) => {
        toast.success(response.message ?? 'Connection deleted successfully')
        getMetadata()
        if (connection.connectionType.toLowerCase() === 'quickbooks') {
            // Return a custom button for Quickbooks with an icon
            // window.open('https://www.scoopanalytics.com/qbdisconnect', '_blank', 'noreferrer')
            // Gabe: 12/9 - QB told us to not redirect to the disconnect page
            // TO-DO: need to fire up QuickBooks Connect modal (can switch screens on user)\
            setSelectedInbox(null)
            setAPIOpen(true)
        }
    }

    const handleExtract = (inboxID) => {
        const server = new Server(workspaceID, userID, token)
        server.sheetPostData(
            {
                action: 'runAPIExtract',
                inboxID: inboxID,
            },
            () => {
                checkStatus()
            }
        )
        handleClose()
    }

    const handleWriteBack = (inboxID) => {
        const server = new Server(workspaceID, userID, token)
        server.sheetPostData(
            {
                action: 'writeBack',
                inboxID: inboxID,
            },
            (response) => {
                if (response.error) {
                    toast.error(response.error)
                }
                checkStatus()
            }
        )
        handleClose()
    }

    const handleAfterDelete = (response) => {
        toast.success(response.message ?? 'Dataset deleted successfully')
        getMetadata()
    }

    const handleError = (response) => {
        toast.error(response.message ?? 'There was an error deleting the Dataset')
    }

    const handleNewDataset = (openFn) => {
        const { workspaceName } = workspaceMetadata
        if (workspaceName && workspaceName.includes('Tour') && accountID !== 'A1') {
            tourAlertModal.show({ item: 'dataset' })
        } else {
            openFn()
        }
    }

    let hasWriteback = false
    if (workspaceMetadata && workspaceMetadata.apiConnections) {
        for (let i = 0; i < workspaceMetadata.apiConnections.length; i++) {
            if (workspaceMetadata.apiConnections[i].supportsWriteback) {
                hasWriteback = true
            }
        }
    }

    const hasExtract = selectedInbox?.inboxType === 'robot' && selectedInbox?.botService === 'R19'

    const handleRunBot = () => {
        runBot({
            inboxID: selectedInbox.inboxID,
            onSuccess: (results) => {
                if (results?.status === 'success') {
                    toast.success('App Connector ran successfully!')
                } else if (results?.status === 'error' || !results) {
                    toast.info(
                        'The process is taking some time, your new data will become available soon.'
                    )
                }
            },
            onError: (error) => {
                console.error('RunBot error:', error) // Debug log
                toast.info(
                    'The process is taking some time, your new data will become available soon.'
                )
            },
        })
    }

    const handleSearchChange = (search) => {
        const trimmedSearch = search.trim()
        setDatasetSearch(search)
        const filtered = workspaceMetadata.inboxes
            .filter((inbox) => inbox.label.toLowerCase().includes(trimmedSearch.toLowerCase()))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
        setFilteredInboxes(filtered)
    }

    return (
        <>
            <ScoopScreenLayout
                title={
                    <>
                        Datasets
                        <HelpBotPromptButton
                            prompt={HELP_BOT_PROMPTS.DATASET_SCREEN}
                            variant="icon"
                            iconMinimal
                            tooltip="Turn your raw reports into structured datasets. Click to learn more."
                            tooltipPlacement="right"
                        />
                    </>
                }
                className={styles.datasetIndexScreen}
                icon={<DatabaseIcon size={24} className={styles.headerIconRed} />}
                withSearchBar={!isLoading && workspaceMetadata?.inboxes?.length > 0}
                searchPlaceholder="Search Datasets"
                searchValue={datasetSearch}
                onSearchChange={handleSearchChange}
                searchWidth="400px"
                headerActions={
                    !isLoading &&
                    workspaceMetadata?.inboxes?.length > 0 && (
                        <>
                            {workspaceMetadata &&
                                workspaceMetadata.apiConnections &&
                                workspaceMetadata.apiConnections.length > 0 && (
                                    <ScoopButton
                                        size="sm"
                                        variant="text"
                                        onClick={() =>
                                            apiConnectionsModal.show({
                                                workspaceMetadata,
                                                onDeleteConnection: handleConfirmDeleteConnection,
                                            })
                                        }
                                    >
                                        Connections
                                    </ScoopButton>
                                )}
                            {!isDefaultWorkspace && (
                                <ScoopButton
                                    variant="grey"
                                    size="sm"
                                    leftSection={
                                        <img
                                            src={SlackIcon}
                                            alt="Slack"
                                            className={styles.slackButtonIcon}
                                        />
                                    }
                                    onClick={() =>
                                        hasConnectionToSlack
                                            ? disconnectSlackModal.show({
                                                  onConfirm: handleDisconnectSlack,
                                              })
                                            : connectSlackModal.show({
                                                  onConnect: handleConnectToSlack,
                                                  isLoading: isGettingOAuthUrl,
                                              })
                                    }
                                >
                                    {hasConnectionToSlack ? (
                                        <>
                                            Disconnect <b>Slack</b>
                                        </>
                                    ) : (
                                        <>
                                            Connect to <b>Slack</b>
                                        </>
                                    )}
                                </ScoopButton>
                            )}
                            <div ref={datasetOptionsRef}>
                                <ScoopButton
                                    variant="default"
                                    size="sm"
                                    leftSection={<PlusIcon size={18} />}
                                    onClick={() =>
                                        handleNewDataset(() => setOpenDatasetOptions(true))
                                    }
                                >
                                    New Dataset
                                </ScoopButton>
                            </div>
                        </>
                    )
                }
            >
                {filteredInboxes?.length > 0 ? (
                    filteredInboxes.map((inbox, index) => (
                        <DatasetRow
                            key={'row-' + index}
                            id={index}
                            dataset={inbox}
                            setAnchorEl={setAnchorEl}
                            setSelectedInbox={setSelectedInbox}
                            processStatus={processStatus}
                            isRunning={isRunning && inbox.inboxID === selectedInbox?.inboxID}
                        />
                    ))
                ) : workspaceMetadata?.inboxes?.length > 0 ? (
                    <ScoopText>No datasets found.</ScoopText>
                ) : (
                    <ScoopCenter className={styles.emptyStateContainer}>
                        {isLoading ? (
                            <ScoopColumn
                                className={`${styles.loadingContainer} ${styles.loadingCentered}`}
                            >
                                {[...Array(8)].map((_, index) => (
                                    <ScoopSkeleton
                                        key={index}
                                        variant={'rounded'}
                                        width={'100%'}
                                        height={50}
                                    />
                                ))}
                            </ScoopColumn>
                        ) : (
                            <EmptyDataset
                                title={'Welcome to Datasets'}
                                description={
                                    'Transform your data into clear, compelling visuals with Scoop. Connect securely to share stories that inspire action.'
                                }
                                iconComponent={
                                    <div className={styles.emptyIconWrapper}>
                                        <DatabaseIcon
                                            size={32}
                                            weight="duotone"
                                            className={styles.iconDark}
                                        />
                                    </div>
                                }
                                onClick={() => handleNewDataset(() => setOpenDatasetSources(true))}
                            />
                        )}
                    </ScoopCenter>
                )}
            </ScoopScreenLayout>
            <ScoopMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className={styles.menu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <ScoopMenuItem
                    onClick={handleReprocess}
                    leftSection={<ArrowsCounterClockwiseIcon size={20} weight="regular" />}
                >
                    <ScoopText className={styles.menuItemText}>Reprocess data</ScoopText>
                </ScoopMenuItem>
                {selectedInbox && selectedInbox.extractDefinition && (
                    <ScoopMenuItem
                        onClick={() => {
                            trackEvent(EVENTS.DATASET.MODIFY_API_CONNECTION)
                            setAPIOpen(true)
                            setAnchorEl(null)
                            setSelectedInbox({ ...selectedInbox })
                        }}
                        leftSection={<PencilSimpleIcon size={20} weight="regular" />}
                    >
                        <ScoopText className={styles.menuItemText}>Modify API Connection</ScoopText>
                    </ScoopMenuItem>
                )}
                {selectedInbox && selectedInbox.extractDefinition && (
                    <ScoopMenuItem
                        onClick={() => {
                            handleExtract(selectedInbox.inboxID)
                        }}
                        leftSection={<DownloadSimpleIcon size={20} weight="regular" />}
                    >
                        <ScoopText className={styles.menuItemText}>Run API Extract Now</ScoopText>
                    </ScoopMenuItem>
                )}
                {hasExtract && (
                    <ScoopMenuItem
                        onClick={() => {
                            handleRunBot()
                            setAnchorEl(null)
                        }}
                        leftSection={<ArrowSquareOutIcon size={20} weight="regular" />}
                    >
                        <ScoopText className={styles.menuItemText}>Extract data now</ScoopText>
                    </ScoopMenuItem>
                )}
                {hasConnectionToSlack && (
                    <ScoopMenuItem
                        onClick={() => {
                            configureSlackModal.show({
                                inboxID: selectedInbox?.inboxID,
                                inboxName: selectedInbox?.label,
                                slackConnectionKey,
                            })
                            setAnchorEl(null)
                        }}
                        leftSection={<SlackLogoIcon size={20} weight="regular" />}
                    >
                        <ScoopText className={styles.menuItemText}>
                            Configure Slack sharing
                        </ScoopText>
                    </ScoopMenuItem>
                )}
                {hasWriteback && !hasExtract && (
                    <>
                        <ScoopMenuItem
                            onClick={() => {
                                trackEvent(EVENTS.DATASET.SETUP_WRITEBACK)
                                setWritebackOpen(true)
                                setAnchorEl(null)
                            }}
                            leftSection={<EyeIcon size={20} weight="regular" />}
                        >
                            <ScoopText className={styles.menuItemText}>
                                {(selectedInbox && selectedInbox.writebackDefinition
                                    ? 'Modify'
                                    : 'Setup') + ' Application Writeback'}
                            </ScoopText>
                        </ScoopMenuItem>
                        {selectedInbox && selectedInbox.writebackDefinition && (
                            <ScoopMenuItem
                                onClick={() => {
                                    handleWriteBack(selectedInbox.inboxID)
                                    setAnchorEl(null)
                                }}
                                leftSection={<UploadSimpleIcon size={20} weight="regular" />}
                            >
                                <ScoopText className={styles.menuItemText}>
                                    Write Back to Application Now
                                </ScoopText>
                            </ScoopMenuItem>
                        )}
                    </>
                )}

                <ScoopMenuItem
                    onClick={handleDeleteInbox}
                    className={styles.deleteMenuItem}
                    leftSection={
                        <TrashIcon size={20} weight="regular" className={styles.deleteIcon} />
                    }
                >
                    <ScoopText className={styles.deleteMenuItemText}>Delete Dataset</ScoopText>
                </ScoopMenuItem>
            </ScoopMenu>
            <NewDatasetModal
                open={openDatasetSources || openBlendedDataset}
                workspaceMetadata={workspaceMetadata}
                onClose={() => {
                    setOpenDatasetSources(false)
                    setOpenBlendedDataset(false)
                    getMetadata()
                    checkStatus()
                }}
                getMetadata={getMetadata}
                openBlendedDataset={openBlendedDataset}
            />
            <APIConnector
                selectedInbox={selectedInbox}
                setSelectedInbox={setSelectedInbox}
                open={apiOpen}
                onClose={() => {
                    setAPIOpen(false)
                }}
                stepBack={() => {
                    setAPIOpen(false)
                }}
                connectorType={selectedInbox?.extractDefinition?.connectorType || 'Quickbooks'}
                getMetadata={getMetadata}
                showSnackbar={() => {
                    toast.success(
                        'Dataset modified successfully! Data reprocessing will begin soon.'
                    )
                }}
                setReprocessing={() => setReprocessing(true)}
            />
            <APIWriteBack
                selectedInbox={selectedInbox}
                setSelectedInbox={setSelectedInbox}
                metadata={workspaceMetadata}
                open={writebackOpen}
                stepBack={() => {
                    setWritebackOpen(false)
                }}
                onClose={() => {
                    setWritebackOpen(false)
                }}
            />
            <ScoopMenu
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={openDatasetOptions}
                anchorEl={datasetOptionsRef.current}
                className={styles.datasetOptionsMenu}
                onClose={() => setOpenDatasetOptions(false)}
            >
                <DialogItem
                    title={'Import from Source'}
                    description={
                        'Connect to a source system or upload a file to start a new dataset'
                    }
                    icon={<TableIcon size={24} weight="duotone" className={styles.iconPrimary} />}
                    onClick={() => {
                        setOpenDatasetSources(true)
                        setOpenDatasetOptions(false)
                    }}
                />
                {!workspaceMetadata?.account?.accountType?.startsWith('appsumo') && (
                    <DialogItem
                        title={'Build from Existing'}
                        description={'Create a new dataset by combining one or more existing ones'}
                        icon={
                            <ChartBarIcon
                                size={24}
                                weight="duotone"
                                className={styles.iconPrimary}
                            />
                        }
                        onClick={() => {
                            setOpenBlendedDataset(true)
                            setOpenDatasetOptions(false)
                        }}
                    />
                )}
            </ScoopMenu>
        </>
    )
}
